/**
 * 乒乓球游戏入口
 *
 * P1（玩家）在右侧，P2（Bot）在左侧。
 * 当嵌入 pixijs-aibot 的 iframe 时，P2 由宿主通过 postMessage 控制。
 * 独立运行时，P2 使用内置简单 AI。
 */

import Phaser from 'phaser'
import { PingPongScene } from './game/PingPongScene'
import { GameBridge } from './platform/Bridge'
import { initPingPongSidePanel } from './ui/PingPongSidePanel'

// 全局 bridge 实例，场景通过 registry 获取
const bridge = new GameBridge()

const START_BGM_URL = '/assets/audio/start-bgm.mp3?v=20260507'
const GAME_BGM_URL = '/assets/audio/game-bgm.mp3?v=20260507'
const START_BUTTON_SFX_URL = '/assets/audio/start-button.mp3?v=20260507'
const START_VIDEO_URL = '/assets/start/start-bg-mobile.mp4?v=20260512'
const START_VIDEO_POSTER_URL = '/assets/start/start-bg-mobile-poster.jpg?v=20260512'
const START_BGM_VOLUME = 0.08
const GAME_BGM_VOLUME = 0.08
const GAME_BGM_DUCK_VOLUME = 0.035
const START_BUTTON_SFX_VOLUME = 0.78

const config: Phaser.Types.Core.GameConfig = {
  type: Phaser.AUTO,
  parent: 'game-container',
  width: 960,
  height: 540,
  backgroundColor: '#0a0a1a',
  antialias: true,
  antialiasGL: true,
  roundPixels: true,
  scale: {
    mode: Phaser.Scale.FIT,
    autoCenter: Phaser.Scale.CENTER_BOTH,
  },
  scene: [PingPongScene],
  callbacks: {
    preBoot: (game) => {
      // 通过 registry 传递 bridge 给场景
      game.registry.set('bridge', bridge)
    },
  },
}

const startOverlay = document.getElementById('start-overlay')
const startButton = document.getElementById('start-button') as HTMLButtonElement | null
const startVideo = document.getElementById('start-video') as HTMLVideoElement | null
let arenaReady = false
let waitingForArena = false

type MobileLeaderboardBoard = 'index' | 'rally'
type MobileDifficulty = 'easy' | 'normal' | 'hard' | 'hell'

interface MobileLeaderboardEntry {
  name: string
  score: number
  meta: string
}

interface MobileCompetitiveBest {
  score: number
  rating: string
  title: string
  difficulty: MobileDifficulty
  difficultyLabel: string
  maxRally: number
  p1: number
  p2: number
  updatedAt: number
}

type MobileBridgeMessage = {
  type?: string
  reason?: string
  p1?: number
  p2?: number
  p1Energy?: number
  rallyCount?: number
  maxRallyCount?: number
  evaluation?: string
}

const MOBILE_LEADERBOARDS: Record<MobileLeaderboardBoard, MobileLeaderboardEntry[]> = {
  index: [
    { name: '咸鱼', score: 872, meta: 'S+ 宗师' },
    { name: 'KongKong', score: 846, meta: 'S+ 宗师' },
    { name: '咖啡', score: 678, meta: 'S 大师' },
    { name: 'TT', score: 642, meta: 'A+ 精英' },
    { name: 'xing', score: 588, meta: 'A+ 精英' },
    { name: '哈哈哈儿', score: 521, meta: 'A+ 精英' },
    { name: 'kimi', score: 466, meta: 'A 强者' },
  ],
  rally: [
    { name: 'KongKong', score: 17, meta: '最高连击' },
    { name: '咸鱼', score: 16, meta: '最高连击' },
    { name: '咖啡', score: 15, meta: '最高连击' },
    { name: 'TT', score: 14, meta: '最高连击' },
    { name: '哈哈哈儿', score: 13, meta: '最高连击' },
    { name: 'xing', score: 11, meta: '最高连击' },
    { name: 'kimi', score: 9, meta: '最高连击' },
  ],
}

const MOBILE_RANK_CONTEXT: Record<MobileLeaderboardBoard, string> = {
  index: '按历史最高单局竞技指数排序，同分看难度与连击。',
  rally: '按历史最高连续接球数排序，第一名控制在 17 连击以内。',
}

const MOBILE_RANK_TAG: Record<MobileLeaderboardBoard, string> = {
  index: 'INDEX TOP 50',
  rally: 'COMBO TOP 50',
}

const MOBILE_PLAYER_NAME_KEY = 'pingPong.mobile.playerName'
const MOBILE_COMPETITIVE_BEST_KEY = 'pingPong.mobile.competitiveBest.v1'
const MOBILE_RECENT_MATCHES_KEY = 'pingPong.mobile.recentMatches.v1'
const MOBILE_DEFAULT_PLAYER_NAME = 'Jin'
const MOBILE_DIFFICULTY_LABELS: Record<MobileDifficulty, string> = {
  easy: '容易',
  normal: '中等',
  hard: '困难',
  hell: '地狱',
}
const MOBILE_DIFFICULTY_MULTIPLIER: Record<MobileDifficulty, number> = {
  easy: 0.7,
  normal: 1,
  hard: 1.2,
  hell: 1.4,
}
const MOBILE_DIFFICULTY_CAP: Record<MobileDifficulty, number> = {
  easy: 500,
  normal: 780,
  hard: 900,
  hell: 999,
}

const startBgm = new Audio(START_BGM_URL)
const gameBgm = new Audio(GAME_BGM_URL)
const startButtonSfx = new Audio(START_BUTTON_SFX_URL)
const fadeFrames = new WeakMap<HTMLMediaElement, number>()
let startAudioUnlocked = false
let gameBgmWanted = false
let gameBgmRecoveryArmed = false
let gameBgmDucked = false
let returnHitDuckTimer: number | null = null

startBgm.loop = true
startBgm.preload = 'auto'
startBgm.volume = 0
gameBgm.loop = true
gameBgm.preload = 'auto'
gameBgm.volume = 0
gameBgm.addEventListener('pause', () => {
  if (!gameBgmWanted || document.hidden) return
  window.setTimeout(recoverGameBgm, 80)
})
startButtonSfx.preload = 'auto'
startButtonSfx.volume = START_BUTTON_SFX_VOLUME

function clampVolume(value: number): number {
  return Math.max(0, Math.min(value, 1))
}

function fadeAudio(audio: HTMLMediaElement, targetVolume: number, durationMs: number, onComplete?: () => void): void {
  const activeFrame = fadeFrames.get(audio)
  if (activeFrame !== undefined) {
    cancelAnimationFrame(activeFrame)
  }

  const from = audio.volume
  const target = clampVolume(targetVolume)
  const duration = Math.max(durationMs, 1)
  const startedAt = performance.now()

  const tick = (time: number): void => {
    const t = Math.min((time - startedAt) / duration, 1)
    const eased = 1 - Math.pow(1 - t, 3)
    audio.volume = clampVolume(Phaser.Math.Linear(from, target, eased))

    if (t < 1) {
      fadeFrames.set(audio, requestAnimationFrame(tick))
      return
    }

    fadeFrames.delete(audio)
    onComplete?.()
  }

  fadeFrames.set(audio, requestAnimationFrame(tick))
}

function playLoopingAudio(audio: HTMLMediaElement, targetVolume: number, fadeMs: number): Promise<boolean> {
  if (!audio.paused) {
    fadeAudio(audio, targetVolume, fadeMs)
    return Promise.resolve(true)
  }

  audio.volume = 0
  return audio.play()
    .then(() => fadeAudio(audio, targetVolume, fadeMs))
    .then(() => true)
    .catch(() => {
      // Browsers may block audio until a user gesture; the unlock handler retries.
      return false
    })
}

function fadeOutAndPause(audio: HTMLMediaElement, durationMs: number): void {
  fadeAudio(audio, 0, durationMs, () => {
    audio.pause()
  })
}

function playStartBgm(): Promise<boolean> {
  if (startOverlay?.classList.contains('is-hidden')) return Promise.resolve(false)
  return playLoopingAudio(startBgm, START_BGM_VOLUME, 900)
}

function disarmGameBgmRecovery(): void {
  if (!gameBgmRecoveryArmed) return
  gameBgmRecoveryArmed = false
  window.removeEventListener('pointerdown', recoverGameBgm, true)
  window.removeEventListener('pointerup', recoverGameBgm, true)
  window.removeEventListener('click', recoverGameBgm, true)
  window.removeEventListener('touchstart', recoverGameBgm, true)
  window.removeEventListener('visibilitychange', recoverGameBgm)
  window.removeEventListener('focus', recoverGameBgm)
}

function armGameBgmRecovery(): void {
  if (gameBgmRecoveryArmed) return
  gameBgmRecoveryArmed = true
  window.addEventListener('pointerdown', recoverGameBgm, true)
  window.addEventListener('pointerup', recoverGameBgm, true)
  window.addEventListener('click', recoverGameBgm, true)
  window.addEventListener('touchstart', recoverGameBgm, true)
  window.addEventListener('visibilitychange', recoverGameBgm)
  window.addEventListener('focus', recoverGameBgm)
}

function getGameBgmTargetVolume(): number {
  return gameBgmDucked ? GAME_BGM_DUCK_VOLUME : GAME_BGM_VOLUME
}

function ensureGameBgm(targetVolume = getGameBgmTargetVolume(), fadeMs = 520): void {
  if (!gameBgmWanted || document.hidden) return
  void playLoopingAudio(gameBgm, targetVolume, fadeMs).then((played) => {
    if (played) {
      disarmGameBgmRecovery()
      return
    }
    armGameBgmRecovery()
  })
}

function recoverGameBgm(): void {
  if (!gameBgmWanted || document.hidden) return
  ensureGameBgm(getGameBgmTargetVolume(), 260)
}

function playGameBgm(): void {
  gameBgmWanted = true
  gameBgmDucked = false
  ensureGameBgm(GAME_BGM_VOLUME, 900)
}

function duckGameBgm(): void {
  if (!gameBgmWanted) return
  gameBgmDucked = true
  ensureGameBgm(GAME_BGM_DUCK_VOLUME, 180)
}

function restoreGameBgm(): void {
  gameBgmDucked = false
  ensureGameBgm(GAME_BGM_VOLUME, 520)
}

function duckGameBgmForReturnHit(event: Event): void {
  if (!gameBgmWanted || gameBgmDucked) return
  const tier = Math.max(1, Math.min((event as CustomEvent<{ tier?: number }>).detail?.tier ?? 1, 4))
  ensureGameBgm(Math.max(0.018, GAME_BGM_VOLUME - 0.018 - tier * 0.006), 45)
  if (returnHitDuckTimer !== null) {
    window.clearTimeout(returnHitDuckTimer)
  }
  returnHitDuckTimer = window.setTimeout(() => {
    returnHitDuckTimer = null
    ensureGameBgm(GAME_BGM_VOLUME, 130)
  }, 115 + tier * 18)
}

function playStartButtonSfx(): void {
  startButtonSfx.currentTime = 0
  startButtonSfx.volume = START_BUTTON_SFX_VOLUME
  startButtonSfx.play().catch(() => {})
}

function setText(id: string, value: string): void {
  const el = document.getElementById(id)
  if (el) el.textContent = value
}

function clampNumber(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value))
}

function normalizeMobileDifficulty(level: unknown): MobileDifficulty {
  if (level === 'easy' || level === 'normal' || level === 'hard' || level === 'hell') return level
  return 'normal'
}

function sanitizeMobilePlayerName(value: string): string {
  return value.trim().replace(/\s+/g, ' ').slice(0, 12) || MOBILE_DEFAULT_PLAYER_NAME
}

function getMobilePlayerName(): string {
  try {
    return sanitizeMobilePlayerName(localStorage.getItem(MOBILE_PLAYER_NAME_KEY) || MOBILE_DEFAULT_PLAYER_NAME)
  } catch {
    return MOBILE_DEFAULT_PLAYER_NAME
  }
}

function saveMobilePlayerName(name: string): void {
  try {
    localStorage.setItem(MOBILE_PLAYER_NAME_KEY, sanitizeMobilePlayerName(name))
  } catch {
    /* ignore */
  }
}

function readMobileCompetitiveBest(): MobileCompetitiveBest | null {
  try {
    const raw = localStorage.getItem(MOBILE_COMPETITIVE_BEST_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<MobileCompetitiveBest>
    if (typeof parsed.score !== 'number' || parsed.score <= 0) return null
    return {
      score: Math.round(parsed.score),
      rating: parsed.rating || 'C',
      title: parsed.title || '入门',
      difficulty: normalizeMobileDifficulty(parsed.difficulty),
      difficultyLabel: parsed.difficultyLabel || MOBILE_DIFFICULTY_LABELS[normalizeMobileDifficulty(parsed.difficulty)],
      maxRally: Math.max(0, Math.round(parsed.maxRally || 0)),
      p1: Math.max(0, Math.round(parsed.p1 || 0)),
      p2: Math.max(0, Math.round(parsed.p2 || 0)),
      updatedAt: parsed.updatedAt || Date.now(),
    }
  } catch {
    return null
  }
}

function saveMobileCompetitiveBest(best: MobileCompetitiveBest): void {
  try {
    localStorage.setItem(MOBILE_COMPETITIVE_BEST_KEY, JSON.stringify(best))
  } catch {
    /* ignore */
  }
}

function readMobileRecentMatches(): MobileCompetitiveBest[] {
  try {
    const raw = localStorage.getItem(MOBILE_RECENT_MATCHES_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed
      .map((item) => {
        if (!item || typeof item.score !== 'number') return null
        const difficulty = normalizeMobileDifficulty(item.difficulty)
        return {
          score: Math.round(item.score),
          rating: item.rating || 'C',
          title: item.title || '入门',
          difficulty,
          difficultyLabel: item.difficultyLabel || MOBILE_DIFFICULTY_LABELS[difficulty],
          maxRally: Math.max(0, Math.round(item.maxRally || 0)),
          p1: Math.max(0, Math.round(item.p1 || 0)),
          p2: Math.max(0, Math.round(item.p2 || 0)),
          updatedAt: item.updatedAt || Date.now(),
        } satisfies MobileCompetitiveBest
      })
      .filter((item): item is MobileCompetitiveBest => Boolean(item))
      .slice(0, 5)
  } catch {
    return []
  }
}

function saveMobileRecentMatches(matches: MobileCompetitiveBest[]): void {
  try {
    localStorage.setItem(MOBILE_RECENT_MATCHES_KEY, JSON.stringify(matches.slice(0, 5)))
  } catch {
    /* ignore */
  }
}

function getMobileCompetitiveRating(score: number): { rating: string; title: string; min: number; max: number } {
  if (score >= 900) return { rating: 'SS', title: '王者', min: 900, max: 999 }
  if (score >= 780) return { rating: 'S+', title: '宗师', min: 780, max: 899 }
  if (score >= 650) return { rating: 'S', title: '大师', min: 650, max: 779 }
  if (score >= 500) return { rating: 'A+', title: '精英', min: 500, max: 649 }
  if (score >= 350) return { rating: 'A', title: '强者', min: 350, max: 499 }
  if (score >= 200) return { rating: 'B', title: '熟练', min: 200, max: 349 }
  return { rating: 'C', title: '入门', min: 0, max: 199 }
}

function getMobileRatingProgress(score: number): number {
  const rating = getMobileCompetitiveRating(score)
  const span = Math.max(1, rating.max - rating.min)
  return clampNumber(((score - rating.min) / span) * 100, 4, 100)
}

function computeMobileCompetitiveScore(
  p1: number,
  p2: number,
  maxRally: number,
  difficulty: MobileDifficulty
): MobileCompetitiveBest {
  const totalScore = Math.max(1, p1 + p2)
  const scoreShare = p1 / totalScore
  const won = p1 > p2
  const margin = p1 - p2
  const comboScore = clampNumber((maxRally / 18) * 100, 0, 100)
  const stabilityScore = clampNumber(scoreShare * 100 + (won ? 10 : -8), 0, 100)
  const resultScore = clampNumber((won ? 68 : 22) + margin * 3.6 + scoreShare * 24, 0, 100)
  const reactionScore = clampNumber(42 + maxRally * 3.2 + (won ? 8 : 0), 0, 100)
  const predictionScore = clampNumber(38 + maxRally * 2.8 + Math.max(0, margin) * 2, 0, 100)
  const baseScore =
    comboScore * 0.22 +
    stabilityScore * 0.2 +
    resultScore * 0.34 +
    reactionScore * 0.14 +
    predictionScore * 0.1
  const rawScore = Math.round(baseScore * 7.1 * MOBILE_DIFFICULTY_MULTIPLIER[difficulty])
  const score = Math.min(rawScore, MOBILE_DIFFICULTY_CAP[difficulty], 999)
  const rating = getMobileCompetitiveRating(score)

  return {
    score,
    rating: rating.rating,
    title: rating.title,
    difficulty,
    difficultyLabel: MOBILE_DIFFICULTY_LABELS[difficulty],
    maxRally,
    p1,
    p2,
    updatedAt: Date.now(),
  }
}

function getMobileDifficultyRank(difficulty: MobileDifficulty): number {
  return difficulty === 'hell' ? 4 : difficulty === 'hard' ? 3 : difficulty === 'normal' ? 2 : 1
}

function getMobilePerformanceMetrics(match: MobileCompetitiveBest | null): Record<string, { value: string; detail: string; score: number }> {
  if (!match) {
    return {
      reaction: { value: '--', detail: '样本不足', score: 0 },
      preposition: { value: '--', detail: '样本不足', score: 0 },
      pressure: { value: '--', detail: '样本不足', score: 0 },
      defense: { value: '--', detail: '样本不足', score: 0 },
      return: { value: '--', detail: '样本不足', score: 0 },
      control: { value: '--', detail: '样本不足', score: 0 },
      tempo: { value: '--', detail: '样本不足', score: 0 },
      spin: { value: '--', detail: '样本不足', score: 0 },
      offense: { value: '--', detail: '样本不足', score: 0 },
      keypoint: { value: '--', detail: '样本不足', score: 0 },
      clutch: { value: '--', detail: '样本不足', score: 0 },
      curve: { value: '--', detail: '样本不足', score: 0 },
    }
  }

  const rank = getMobileDifficultyRank(match.difficulty)
  const total = Math.max(1, match.p1 + match.p2)
  const winShare = match.p1 / total
  const margin = match.p1 - match.p2
  const won = match.p1 > match.p2
  const rallyFactor = clampNumber(match.maxRally / 18, 0, 1)
  const reactionMs = Math.round(clampNumber(520 - match.maxRally * 13 - rank * 28 - (won ? 24 : 0), 165, 520))
  const defense = Math.round(clampNumber(winShare * 72 + rallyFactor * 38 + (won ? 6 : -8), 0, 100))
  const preposition = Math.round(clampNumber(32 + rallyFactor * 45 + rank * 5 + Math.max(0, margin) * 2.2, 0, 100))
  const pressure = Math.round(clampNumber(28 + rallyFactor * 42 + rank * 7 + Math.max(0, margin) * 2.4, 0, 100))
  const returnQuality = Math.round(clampNumber(34 + rallyFactor * 44 + winShare * 22 + rank * 3, 0, 100))
  const control = Math.round(clampNumber(defense * 0.68 + preposition * 0.32, 0, 100))
  const tempo = Math.round(clampNumber(30 + rallyFactor * 45 + rank * 8, 0, 100))
  const spin = Math.round(clampNumber(26 + rallyFactor * 36 + Math.max(0, margin) * 3 + rank * 6, 0, 100))
  const offense = Math.round(clampNumber((won ? 48 : 20) + Math.max(0, margin) * 6 + rallyFactor * 28, 0, 100))
  const keypoint = Math.round(clampNumber(36 + (won ? 26 : 0) + Math.max(0, margin) * 3 + rank * 4, 0, 100))
  const clutch = Math.round(clampNumber(24 + rallyFactor * 55 + rank * 5, 0, 100))
  const curve = Math.round(clampNumber(20 + rallyFactor * 58 + pressure * 0.18, 0, 100))

  return {
    reaction: { value: `${reactionMs}ms`, detail: '威胁反应', score: Math.round(100 - ((reactionMs - 165) / 355) * 100) },
    preposition: { value: `${preposition}%`, detail: `${Math.round(preposition / 4)}/${25} 站对`, score: preposition },
    pressure: { value: `${pressure}%`, detail: '高压接回', score: pressure },
    defense: { value: `${defense}%`, detail: `${match.p1}/${total} 接回`, score: defense },
    return: { value: String(returnQuality), detail: `均速 ${Math.round(1180 + match.maxRally * 32)}`, score: returnQuality },
    control: { value: `${control}%`, detail: `${Math.round(control / 8)}/${13} 中心`, score: control },
    tempo: { value: String(tempo), detail: `高速 ${Math.round(tempo / 2)}%`, score: tempo },
    spin: { value: String(spin), detail: `转化 ${Math.round(spin / 9)}/${11}`, score: spin },
    offense: { value: `${offense}%`, detail: `${Math.max(0, margin)}/${Math.max(1, match.p1)} 转化`, score: offense },
    keypoint: { value: `${keypoint}%`, detail: won ? '关键分胜' : '关键分待提升', score: keypoint },
    clutch: { value: `${Math.max(1, Math.round(match.maxRally / 2))}次`, detail: '最佳剩余', score: clutch },
    curve: { value: `${Math.max(0, Math.round(match.maxRally * 0.7))}次`, detail: '高质量甩球', score: curve },
  }
}

function renderMobileCompetitiveBest(best: MobileCompetitiveBest | null): void {
  const progress = document.getElementById('mobile-competitive-progress')

  if (!best) {
    setText('mobile-competitive-score', '0')
    setText('mobile-competitive-rating', 'C 入门')
    setText('mobile-competitive-trend', '完成一局后生成指数')
    setText('mobile-competitive-cert', '暂无难度')
    setText('mobile-competitive-progress-start', '--')
    setText('mobile-competitive-progress-end', '--')
    progress?.style.setProperty('width', '0%')
    return
  }

  const rating = getMobileCompetitiveRating(best.score)
  const nextText = best.score >= 999
    ? '已到顶级'
    : `距 ${rating.rating} 段上限 ${Math.max(0, rating.max - best.score)}`
  setText('mobile-competitive-score', String(best.score))
  setText('mobile-competitive-rating', `${best.rating} ${best.title}`)
  setText('mobile-competitive-trend', nextText)
  setText('mobile-competitive-cert', `${best.difficultyLabel}难度`)
  setText('mobile-competitive-progress-start', `${rating.rating} ${rating.min}`)
  setText('mobile-competitive-progress-end', `${rating.rating} ${rating.max}`)
  progress?.style.setProperty('width', `${Math.round(getMobileRatingProgress(best.score))}%`)
}

let pcSidePanelDrawersReady = false
let pcSidePanelStateBridgeReady = false

function setupMobilePcPanelDrawers(): void {
  if (pcSidePanelDrawersReady) return
  pcSidePanelDrawersReady = true

  const body = document.body
  const leftPanel = document.getElementById('ping-pong-side-panel')
  const rightPanel = document.getElementById('ping-pong-leaderboard-panel')
  const scrim = document.getElementById('mobile-panel-scrim') as HTMLButtonElement | null
  const openSettingsButton = document.getElementById('mobile-open-settings') as HTMLButtonElement | null
  const openRankButton = document.getElementById('mobile-open-rank') as HTMLButtonElement | null
  let panelsLocked = false

  if (!leftPanel || !rightPanel || !scrim || !openSettingsButton || !openRankButton) return

  const updateExpandedState = (): void => {
    const leftOpen = body.dataset.mobileLeftPanel === 'open'
    const rightOpen = body.dataset.mobileRightPanel === 'open'
    openSettingsButton.setAttribute('aria-expanded', String(leftOpen))
    openRankButton.setAttribute('aria-expanded', String(rightOpen))
    leftPanel.setAttribute('aria-hidden', String(!leftOpen))
    rightPanel.setAttribute('aria-hidden', String(!rightOpen))
  }

  const setPanelState = (leftOpen: boolean, rightOpen: boolean, locked = false): void => {
    panelsLocked = locked

    if (leftOpen) body.dataset.mobileLeftPanel = 'open'
    else delete body.dataset.mobileLeftPanel

    if (rightOpen) body.dataset.mobileRightPanel = 'open'
    else delete body.dataset.mobileRightPanel

    if (locked) body.dataset.mobilePanelsLocked = 'true'
    else delete body.dataset.mobilePanelsLocked

    updateExpandedState()
  }

  const closePanels = (force = false): void => {
    if (panelsLocked && !force) return
    setPanelState(false, false, false)
  }

  const openSinglePanel = (side: 'left' | 'right'): void => {
    setPanelState(side === 'left', side === 'right', false)
  }

  const stopPanelEvent = (event: Event): void => {
    event.stopPropagation()
  }

  for (const panel of [leftPanel, rightPanel]) {
    panel.addEventListener('pointerdown', stopPanelEvent)
    panel.addEventListener('pointermove', stopPanelEvent)
    panel.addEventListener('touchstart', stopPanelEvent)
    panel.addEventListener('touchmove', stopPanelEvent)
    panel.addEventListener('click', stopPanelEvent)
  }

  openSettingsButton.addEventListener('click', (event) => {
    event.stopPropagation()
    openSinglePanel('left')
  })

  openRankButton.addEventListener('click', (event) => {
    event.stopPropagation()
    openSinglePanel('right')
  })

  scrim.addEventListener('click', () => closePanels(false))
  window.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closePanels(false)
  })
  window.addEventListener('ping-pong:start', () => closePanels(true))
  window.addEventListener('ping-pong:replay-click', () => closePanels(true))
  window.addEventListener('ping-pong:bridge-message', (event) => {
    const message = (event as CustomEvent<MobileBridgeMessage>).detail
    if (message?.type === 'game_over') setPanelState(true, true, true)
  })

  updateExpandedState()
}

function setupMobilePcSidePanelStateBridge(): void {
  if (pcSidePanelStateBridgeReady) return
  pcSidePanelStateBridgeReady = true

  let difficulty: MobileDifficulty = 'normal'
  let difficultyLabel = MOBILE_DIFFICULTY_LABELS[difficulty]
  let hasStarted = false
  let gameActive = false
  let gameOver = false
  let p1 = 0
  let p2 = 0
  let rallyCount = 0
  let maxRallyCount = 0
  let matchSeed = Date.now()

  const resetMatch = (): void => {
    p1 = 0
    p2 = 0
    rallyCount = 0
    maxRallyCount = 0
    matchSeed = Date.now()
  }

  const buildPerformance = (reason: string) => {
    const totalPoints = Math.max(0, p1 + p2)
    const receiveAttempts = Math.max(maxRallyCount, totalPoints + maxRallyCount, gameOver ? 1 : 0)
    const receiveSuccesses = Math.min(receiveAttempts, Math.max(maxRallyCount, Math.round(receiveAttempts * 0.72)))
    const playerHits = Math.max(maxRallyCount, receiveSuccesses)
    const playerCenterHits = Math.round(playerHits * clampNumber(0.32 + maxRallyCount * 0.025, 0.32, 0.72))
    const prePositionAttempts = Math.max(receiveAttempts, maxRallyCount)
    const prePositionSuccesses = Math.round(prePositionAttempts * clampNumber(0.38 + maxRallyCount * 0.02, 0.38, 0.78))
    const highPressureAttempts = Math.round(receiveAttempts * 0.42)
    const highPressureReturns = Math.round(highPressureAttempts * clampNumber(0.45 + maxRallyCount * 0.025, 0.45, 0.86))
    const reactionSamples = receiveAttempts
    const avgReactionMs = reactionSamples > 0
      ? Math.round(clampNumber(430 - maxRallyCount * 11 + (difficulty === 'hell' ? 16 : 0), 238, 430))
      : null
    const bestReactionMs = avgReactionMs === null ? null : Math.max(160, avgReactionMs - 56)
    const highestBallSpeed = Math.round(600 + Math.max(0, maxRallyCount - 1) * 100)
    const avgReturnSpeed = Math.round(1050 + maxRallyCount * 44)
    const highSpeedReturnRate = clampNumber(0.24 + maxRallyCount * 0.025, 0.24, 0.78)
    const returnQuality = Math.round(clampNumber(42 + maxRallyCount * 3.2, 42, 92))
    const keyPointAttempts = totalPoints >= 16 ? Math.max(1, Math.round(totalPoints / 4)) : 0
    const keyPointWins = keyPointAttempts > 0 ? Math.round(keyPointAttempts * (p1 >= p2 ? 0.62 : 0.36)) : 0

    return {
      matchId: `mobile-${matchSeed}`,
      difficulty,
      p1,
      p2,
      isComplete: gameOver || reason === 'game_over',
      maxRallyCount,
      playerHits,
      playerCenterHits,
      playerReceiveAttempts: receiveAttempts,
      playerReceiveSuccesses: receiveSuccesses,
      prePositionAttempts,
      prePositionSuccesses,
      highPressureAttempts,
      highPressureReturns,
      reactionSamples,
      avgReactionMs,
      bestReactionMs,
      reactionP50Ms: avgReactionMs,
      reactionP75Ms: avgReactionMs === null ? null : avgReactionMs + 42,
      reactionP90Ms: avgReactionMs === null ? null : avgReactionMs + 78,
      highestBallSpeed,
      avgReturnSpeed,
      highSpeedReturnRate,
      returnSpeedSamples: receiveAttempts,
      returnQuality,
      returnQualitySamples: receiveAttempts,
      keyPointAttempts,
      keyPointWins,
      clutchSaveMs: bestReactionMs,
      curveShotCount: Math.round(maxRallyCount * 0.45),
      spinSamples: playerHits,
      spinConversions: Math.round(playerHits * 0.28),
      spinWeakReturns: Math.round(playerHits * 0.18),
      maxSpin: Math.round(maxRallyCount * 0.16),
      spinThreat: Math.round(clampNumber(maxRallyCount * 5, 0, 100)),
      chargeHits: Math.round(playerHits * 0.32),
      chargeThreats: Math.round(playerHits * 0.25),
      chargeConversions: Math.round(playerHits * 0.16),
      skillUses: Math.max(0, Math.round(totalPoints / 5)),
      skillConversions: Math.max(0, Math.round(totalPoints / 8)),
    }
  }

  const emitState = (reason: string): void => {
    window.dispatchEvent(new CustomEvent('ping-pong:state', {
      detail: {
        reason,
        difficulty,
        difficultyLabel,
        canChangeDifficulty: !hasStarted || gameOver,
        hasStarted,
        gameActive,
        gameOver,
        rallyCount,
        maxRallyCount,
        score: { p1, p2 },
        performance: buildPerformance(reason),
      },
    }))
  }

  window.addEventListener('ping-pong:start', () => {
    hasStarted = true
    gameActive = true
    gameOver = false
    resetMatch()
    emitState('start')
  })

  window.addEventListener('ping-pong:replay-click', () => {
    hasStarted = true
    gameActive = true
    gameOver = false
    resetMatch()
    emitState('replay')
  })

  window.addEventListener('ping-pong:difficulty-changed', (event) => {
    const detail = (event as CustomEvent<{ level?: unknown; label?: string }>).detail
    difficulty = normalizeMobileDifficulty(detail?.level)
    difficultyLabel = detail?.label || MOBILE_DIFFICULTY_LABELS[difficulty]
    emitState('difficulty_changed')
  })

  window.addEventListener('ping-pong:bridge-message', (event) => {
    const message = (event as CustomEvent<MobileBridgeMessage>).detail
    if (!message?.type) return

    if (typeof message.p1 === 'number') p1 = message.p1
    if (typeof message.p2 === 'number') p2 = message.p2
    if (typeof message.rallyCount === 'number') {
      rallyCount = message.rallyCount
      maxRallyCount = Math.max(maxRallyCount, rallyCount)
    }
    if (typeof message.maxRallyCount === 'number') {
      maxRallyCount = Math.max(maxRallyCount, message.maxRallyCount)
    }

    if (message.type === 'game_reset') {
      const replaying = hasStarted && !gameOver
      hasStarted = replaying
      gameActive = replaying
      gameOver = false
      resetMatch()
      emitState('reset')
      return
    }

    if (message.type === 'game_over') {
      hasStarted = true
      gameActive = false
      gameOver = true
      emitState('game_over')
      return
    }

    if (message.type === 'combat_state' || message.type === 'scoreUpdate' || message.type === 'point_scored' || message.type === 'rally_end') {
      const passivePrestartState = !hasStarted && (
        message.type === 'scoreUpdate' ||
        (message.type === 'combat_state' && (message.reason === 'ready' || message.reason === 'reset'))
      )
      if (passivePrestartState) {
        gameActive = false
        emitState(message.reason ?? message.type)
        return
      }

      hasStarted = true
      gameActive = !gameOver
      emitState(message.type)
    }
  })

  emitState('boot')
}

function setupMobilePanels(): void {
  const body = document.body
  const leftPanel = document.getElementById('mobile-settings-panel')
  const rightPanel = document.getElementById('mobile-rank-panel')
  const scrim = document.getElementById('mobile-panel-scrim') as HTMLButtonElement | null
  const openSettingsButton = document.getElementById('mobile-open-settings') as HTMLButtonElement | null
  const openRankButton = document.getElementById('mobile-open-rank') as HTMLButtonElement | null
  const rankList = document.getElementById('mobile-rank-list')
  const rankPodium = document.getElementById('mobile-leaderboard-podium')
  const rankEmpty = document.getElementById('mobile-leaderboard-empty')
  const rankContext = document.getElementById('mobile-rank-context')
  const rankTag = document.getElementById('mobile-rank-tag')
  const playerNameEl = document.getElementById('mobile-player-name')
  const playerForm = document.getElementById('mobile-player-form') as HTMLFormElement | null
  const playerInput = document.getElementById('mobile-player-input') as HTMLInputElement | null
  const playerEditButton = document.getElementById('mobile-player-edit') as HTMLButtonElement | null
  const playerSavedName = document.getElementById('mobile-player-saved-name')
  const difficultyButtons = Array.from(document.querySelectorAll<HTMLButtonElement>('[data-mobile-difficulty-option]'))
  const rankTabs = Array.from(document.querySelectorAll<HTMLButtonElement>('[data-mobile-rank-tab]'))
  const performanceTabs = Array.from(document.querySelectorAll<HTMLButtonElement>('[data-mobile-performance-view]'))
  let panelsLocked = false
  let activeRankBoard: MobileLeaderboardBoard = 'index'
  let bestRally = 0
  let currentP1 = 0
  let currentP2 = 0
  let currentDifficulty: MobileDifficulty = 'normal'
  let competitiveBest = readMobileCompetitiveBest()
  let recentMatches = readMobileRecentMatches()
  let currentPerformance: MobileCompetitiveBest | null = null
  let activePerformanceView: 'current' | 'recent' = 'current'

  if (!leftPanel || !rightPanel || !scrim || !openSettingsButton || !openRankButton || !rankList) return

  const stopPanelEvent = (event: Event): void => {
    event.stopPropagation()
  }

  for (const panel of [leftPanel, rightPanel]) {
    panel.addEventListener('pointerdown', stopPanelEvent)
    panel.addEventListener('pointermove', stopPanelEvent)
    panel.addEventListener('touchstart', stopPanelEvent)
    panel.addEventListener('touchmove', stopPanelEvent)
    panel.addEventListener('click', stopPanelEvent)
  }

  const updateExpandedState = (): void => {
    const leftOpen = body.dataset.mobileLeftPanel === 'open'
    const rightOpen = body.dataset.mobileRightPanel === 'open'
    openSettingsButton.setAttribute('aria-expanded', String(leftOpen))
    openRankButton.setAttribute('aria-expanded', String(rightOpen))
    leftPanel.setAttribute('aria-hidden', String(!leftOpen))
    rightPanel.setAttribute('aria-hidden', String(!rightOpen))
  }

  const setPanelState = (leftOpen: boolean, rightOpen: boolean, locked = false): void => {
    panelsLocked = locked

    if (leftOpen) body.dataset.mobileLeftPanel = 'open'
    else delete body.dataset.mobileLeftPanel

    if (rightOpen) body.dataset.mobileRightPanel = 'open'
    else delete body.dataset.mobileRightPanel

    if (locked) body.dataset.mobilePanelsLocked = 'true'
    else delete body.dataset.mobilePanelsLocked

    updateExpandedState()
  }

  const closePanels = (force = false): void => {
    if (panelsLocked && !force) return
    setPanelState(false, false, false)
  }

  const openSinglePanel = (side: 'left' | 'right'): void => {
    setPanelState(side === 'left', side === 'right', false)
  }

  const openGameOverPanels = (): void => {
    setPanelState(true, true, true)
  }

  const renderLeaderboard = (): void => {
    const entries = MOBILE_LEADERBOARDS[activeRankBoard]
    const podiumEntries = entries.slice(0, 3)
    const listEntries = entries.slice(3)
    rankPodium?.replaceChildren()
    rankList.replaceChildren()

    for (const [index, entry] of podiumEntries.entries()) {
      const rank = index + 1
      const card = document.createElement('article')
      card.className = 'podium-card'
      card.dataset.podiumRank = String(rank)

      const rankBadge = document.createElement('img')
      rankBadge.className = `podium-rank podium-rank-${rank}`
      rankBadge.src = `/assets/ui/leaderboard/rank-${rank}.png?v=20260514c`
      rankBadge.alt = `第${rank}名`
      rankBadge.decoding = 'async'

      const info = document.createElement('div')
      info.className = 'podium-info'

      const name = document.createElement('div')
      name.className = 'podium-name'
      name.textContent = entry.name

      const meta = document.createElement('div')
      meta.className = 'podium-label'
      meta.textContent = entry.meta

      const score = document.createElement('strong')
      score.className = 'podium-score'
      score.textContent = String(entry.score)

      info.append(name, meta)
      card.append(rankBadge, info, score)
      rankPodium?.append(card)
    }

    for (const [index, entry] of listEntries.entries()) {
      const rank = index + 4
      const row = document.createElement('li')
      row.className = 'leaderboard-row'
      row.dataset.rank = String(rank)

      const rankEl = document.createElement('span')
      rankEl.className = 'leaderboard-rank'
      rankEl.textContent = String(rank).padStart(2, '0')

      const info = document.createElement('div')
      info.className = 'leaderboard-row-info'

      const name = document.createElement('div')
      name.className = 'leaderboard-player'
      name.textContent = entry.name

      const meta = document.createElement('div')
      meta.className = 'leaderboard-entry-meta'
      meta.textContent = entry.meta

      const score = document.createElement('strong')
      score.className = 'leaderboard-score'
      score.textContent = String(entry.score)

      info.append(name, meta)
      row.append(rankEl, info, score)
      rankList.append(row)
    }

    if (rankEmpty) rankEmpty.hidden = entries.length > 0

    if (rankContext) rankContext.textContent = MOBILE_RANK_CONTEXT[activeRankBoard]
    if (rankTag) rankTag.textContent = MOBILE_RANK_TAG[activeRankBoard]

    for (const tab of rankTabs) {
      const isActive = tab.dataset.mobileRankTab === activeRankBoard
      tab.classList.toggle('is-active', isActive)
      tab.setAttribute('aria-pressed', String(isActive))
    }
  }

  const updateDifficulty = (level: string, label?: string): void => {
    currentDifficulty = normalizeMobileDifficulty(level)
    const difficultyLabel = label || MOBILE_DIFFICULTY_LABELS[currentDifficulty]
    for (const button of difficultyButtons) {
      const isActive = button.dataset.mobileDifficultyOption === currentDifficulty
      button.classList.toggle('is-active', isActive)
      button.setAttribute('aria-pressed', String(isActive))
    }
    setText('mobile-coach-status-difficulty', difficultyLabel)

    if (currentP1 + currentP2 > 0 || bestRally > 0) {
      currentPerformance = computeMobileCompetitiveScore(currentP1, currentP2, bestRally, currentDifficulty)
      renderPanelPerformance()
    }
  }

  const renderPlayerName = (): void => {
    const name = getMobilePlayerName()
    if (playerNameEl) playerNameEl.textContent = name
    if (playerInput) playerInput.value = name
  }

  const getRecentPerformance = (): MobileCompetitiveBest | null => recentMatches[0] ?? competitiveBest

  const setMetric = (id: string, value: string, detail = ''): void => {
    setText(id, value)
    setText(`${id}-detail`, detail)
  }

  function renderCoachSummary(match: MobileCompetitiveBest | null): void {
    const metrics = getMobilePerformanceMetrics(match)
    setText('mobile-coach-reaction-value', metrics.reaction.value)
    setText('mobile-coach-reaction-label', metrics.reaction.detail)

    if (!match) {
      setText('mobile-coach-strong-value', '--')
      setText('mobile-coach-strong-label', '完成一局后生成')
      setText('mobile-coach-weak-value', '--')
      setText('mobile-coach-weak-label', '暂无样本')
      return
    }

    const candidates = [
      { name: '接球稳定', metric: metrics.defense },
      { name: '预判站位', metric: metrics.preposition },
      { name: '高压接球', metric: metrics.pressure },
      { name: '回球质量', metric: metrics.return },
    ].sort((a, b) => b.metric.score - a.metric.score)

    const strong = candidates[0]
    const weak = candidates[candidates.length - 1]
    setText('mobile-coach-strong-value', strong?.name ?? '--')
    setText('mobile-coach-strong-label', strong ? strong.metric.value : '完成一局后生成')
    setText('mobile-coach-weak-value', weak?.name ?? '--')
    setText('mobile-coach-weak-label', weak ? weak.metric.value : '暂无样本')
  }

  function renderAbilityStats(match: MobileCompetitiveBest | null): void {
    const metrics = getMobilePerformanceMetrics(match)
    setText('mobile-ability-panel-title', activePerformanceView === 'current' ? '本局表现' : '上一局表现')

    if (!match) {
      setText('mobile-match-index', '--')
      setText('mobile-match-index-label', '')
      setMetric('mobile-match-best-rally', '--', '')
    } else {
      setText('mobile-match-index', `${match.rating}${match.title} · ${match.score}`)
      setText('mobile-match-index-label', `${match.difficultyLabel} ${match.p1}:${match.p2}`)
      setMetric('mobile-match-best-rally', String(match.maxRally), `${match.difficultyLabel}难度`)
    }

    setMetric('mobile-metric-reaction', metrics.reaction.value, metrics.reaction.detail)
    setMetric('mobile-metric-preposition', metrics.preposition.value, metrics.preposition.detail)
    setMetric('mobile-metric-pressure', metrics.pressure.value, metrics.pressure.detail)
    setMetric('mobile-metric-defense', metrics.defense.value, metrics.defense.detail)
    setMetric('mobile-metric-return', metrics.return.value, metrics.return.detail)
    setMetric('mobile-metric-control', metrics.control.value, metrics.control.detail)
    setMetric('mobile-metric-tempo', metrics.tempo.value, metrics.tempo.detail)
    setMetric('mobile-metric-spin', metrics.spin.value, metrics.spin.detail)
    setMetric('mobile-metric-offense', metrics.offense.value, metrics.offense.detail)
    setMetric('mobile-metric-keypoint', metrics.keypoint.value, metrics.keypoint.detail)
    setMetric('mobile-metric-clutch', metrics.clutch.value, metrics.clutch.detail)
    setMetric('mobile-metric-curve', metrics.curve.value, metrics.curve.detail)
  }

  function renderRecentTrend(): void {
    if (recentMatches.length === 0) {
      setText('mobile-recent-average-rally', '--')
      setText('mobile-recent-win-rate', '--')
      setText('mobile-recent-trend', '--')
      setText('mobile-recent-average-score', '--')
      return
    }

    const averageRally = Math.round(recentMatches.reduce((sum, match) => sum + match.maxRally, 0) / recentMatches.length)
    const wins = recentMatches.filter((match) => match.p1 > match.p2).length
    const averageScore = Math.round(recentMatches.reduce((sum, match) => sum + match.score, 0) / recentMatches.length)
    const trend = recentMatches.length >= 2
      ? recentMatches[0].score - recentMatches[1].score >= 20
        ? '上升'
        : Math.abs(recentMatches[0].score - recentMatches[1].score) <= 12
          ? '稳定'
          : '波动'
      : '新记录'

    setText('mobile-recent-average-rally', String(averageRally))
    setText('mobile-recent-win-rate', `${Math.round((wins / recentMatches.length) * 100)}%`)
    setText('mobile-recent-trend', trend)
    setText('mobile-recent-average-score', String(averageScore))
  }

  function renderPersonalBest(): void {
    const best = competitiveBest
    const bestMetrics = getMobilePerformanceMetrics(best)
    const hellBest = recentMatches
      .filter((match) => match.difficulty === 'hell')
      .reduce((max, match) => Math.max(max, match.maxRally), best?.difficulty === 'hell' ? best.maxRally : 0)

    if (!best) {
      setText('mobile-best-rating', '--')
      setText('mobile-best-rating-detail', '')
      setText('mobile-best-index', '--')
      setText('mobile-best-index-detail', '')
      setText('mobile-best-hell-rally', '--')
      setText('mobile-best-hell-rally-detail', '')
      setText('mobile-best-threat-reaction', '--')
      setText('mobile-best-threat-reaction-detail', '')
      setText('mobile-best-preposition', '--')
      setText('mobile-best-preposition-detail', '')
      setText('mobile-best-curve-shot', '--')
      setText('mobile-best-curve-shot-detail', '')
      return
    }

    setText('mobile-best-rating', `${best.rating} ${best.title}`)
    setText('mobile-best-rating-detail', `${best.difficultyLabel} ${best.p1}:${best.p2}`)
    setText('mobile-best-index', String(best.score))
    setText('mobile-best-index-detail', `${best.difficultyLabel}难度`)
    setText('mobile-best-hell-rally', hellBest > 0 ? String(hellBest) : '--')
    setText('mobile-best-hell-rally-detail', hellBest > 0 ? '地狱难度' : '')
    setText('mobile-best-threat-reaction', bestMetrics.reaction.value)
    setText('mobile-best-threat-reaction-detail', bestMetrics.reaction.detail)
    setText('mobile-best-preposition', bestMetrics.preposition.value)
    setText('mobile-best-preposition-detail', bestMetrics.preposition.detail)
    setText('mobile-best-curve-shot', bestMetrics.curve.value)
    setText('mobile-best-curve-shot-detail', bestMetrics.curve.detail)
  }

  function renderPanelPerformance(): void {
    const viewedMatch = activePerformanceView === 'current' ? currentPerformance : getRecentPerformance()
    renderMobileCompetitiveBest(competitiveBest)
    renderCoachSummary(viewedMatch)
    renderAbilityStats(viewedMatch)
    renderRecentTrend()
    renderPersonalBest()

    for (const tab of performanceTabs) {
      const isActive = tab.dataset.mobilePerformanceView === activePerformanceView
      tab.classList.toggle('is-active', isActive)
      tab.setAttribute('aria-pressed', String(isActive))
    }
  }

  const updateMatchStats = (message: MobileBridgeMessage): void => {
    const p1 = typeof message.p1 === 'number' ? message.p1 : null
    const p2 = typeof message.p2 === 'number' ? message.p2 : null
    const rally = typeof message.rallyCount === 'number' ? message.rallyCount : null
    const gameOverRally = typeof message.maxRallyCount === 'number' ? message.maxRallyCount : null

    if (p1 !== null) currentP1 = p1
    if (p2 !== null) currentP2 = p2
    if (rally !== null) bestRally = Math.max(bestRally, rally)
    if (gameOverRally !== null) bestRally = Math.max(bestRally, gameOverRally)

    if (currentP1 + currentP2 > 0 || bestRally > 0) {
      currentPerformance = computeMobileCompetitiveScore(currentP1, currentP2, bestRally, currentDifficulty)
    }

    if (message.evaluation && currentPerformance) {
      setText('mobile-settings-tag', message.evaluation)
      currentPerformance = computeMobileCompetitiveScore(currentP1, currentP2, bestRally, currentDifficulty)
      recentMatches = [currentPerformance, ...recentMatches].slice(0, 5)
      saveMobileRecentMatches(recentMatches)

      if (!competitiveBest || currentPerformance.score > competitiveBest.score) {
        competitiveBest = currentPerformance
        saveMobileCompetitiveBest(competitiveBest)
      }
    }

    renderPanelPerformance()
  }

  openSettingsButton.addEventListener('click', (event) => {
    event.stopPropagation()
    openSinglePanel('left')
  })

  openRankButton.addEventListener('click', (event) => {
    event.stopPropagation()
    openSinglePanel('right')
  })

  scrim.addEventListener('click', () => closePanels(false))

  window.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closePanels(false)
  })

  window.addEventListener('ping-pong:start', () => {
    setText('mobile-difficulty-status', '结算后可调整')
    closePanels(true)
  })
  window.addEventListener('ping-pong:replay-click', () => {
    const replayDifficulty = currentDifficulty
    bestRally = 0
    currentP1 = 0
    currentP2 = 0
    currentPerformance = null
    setText('mobile-settings-tag', 'PLAYER')
    setText('mobile-difficulty-status', '结算后可调整')
    renderPanelPerformance()
    closePanels(true)
    window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent('ping-pong:set-difficulty', { detail: { level: replayDifficulty } }))
    }, 0)
  })

  window.addEventListener('ping-pong:difficulty-changed', (event) => {
    const detail = (event as CustomEvent<{ level?: string; label?: string }>).detail
    if (!detail?.level) return
    updateDifficulty(detail.level, detail.label)
  })

  window.addEventListener('ping-pong:bridge-message', (event) => {
    const message = (event as CustomEvent<MobileBridgeMessage>).detail
    if (!message?.type) return

    if (message.type === 'combat_state' || message.type === 'scoreUpdate') {
      updateMatchStats(message)
      return
    }

    if (message.type === 'game_reset') {
      bestRally = 0
      currentP1 = 0
      currentP2 = 0
      currentPerformance = null
      setText('mobile-settings-tag', 'PLAYER')
      renderPanelPerformance()
      return
    }

    if (message.type === 'game_over') {
      updateMatchStats(message)
      setText('mobile-difficulty-status', '结算后可调整')
      openGameOverPanels()
    }
  })

  for (const button of difficultyButtons) {
    button.addEventListener('click', () => {
      const level = button.dataset.mobileDifficultyOption
      if (!level) return
      updateDifficulty(level, button.querySelector('strong')?.textContent ?? undefined)
      window.dispatchEvent(new CustomEvent('ping-pong:set-difficulty', { detail: { level } }))
    })
  }

  for (const tab of rankTabs) {
    tab.addEventListener('click', () => {
      const board = tab.dataset.mobileRankTab
      if (board !== 'index' && board !== 'rally') return
      activeRankBoard = board
      renderLeaderboard()
    })
  }

  for (const tab of performanceTabs) {
    tab.addEventListener('click', () => {
      const view = tab.dataset.mobilePerformanceView
      if (view !== 'current' && view !== 'recent') return
      activePerformanceView = view
      renderPanelPerformance()
    })
  }

  playerEditButton?.addEventListener('click', () => {
    playerForm?.removeAttribute('hidden')
    if (playerSavedName) playerSavedName.hidden = true
    playerInput?.focus()
    playerInput?.select()
  })

  playerForm?.addEventListener('submit', (event) => {
    event.preventDefault()
    const name = sanitizeMobilePlayerName(playerInput?.value || MOBILE_DEFAULT_PLAYER_NAME)
    saveMobilePlayerName(name)
    renderPlayerName()
    playerForm.hidden = true
    if (playerSavedName) playerSavedName.hidden = false
  })

  renderPlayerName()
  renderLeaderboard()
  renderPanelPerformance()
  updateExpandedState()
}

function requestLandscapeMode(): void {
  const isCoarsePointer = window.matchMedia?.('(pointer: coarse)').matches ?? false
  if (!isCoarsePointer) return
  const orientation = screen.orientation as ScreenOrientation & {
    lock?: (orientation: string) => Promise<void>
  }

  const lockLandscape = (): void => {
    orientation.lock?.('landscape').catch(() => {})
  }

  if (!document.fullscreenElement && document.documentElement.requestFullscreen) {
    document.documentElement.requestFullscreen()
      .then(lockLandscape)
      .catch(lockLandscape)
    return
  }

  lockLandscape()
}

function unlockStartAudio(): void {
  void playStartBgm().then((played) => {
    if (!played) return
    startAudioUnlocked = true
    window.removeEventListener('pointerdown', unlockStartAudio, true)
    window.removeEventListener('pointerup', unlockStartAudio, true)
    window.removeEventListener('click', unlockStartAudio, true)
    window.removeEventListener('keydown', unlockStartAudio, true)
  })
}

function enterGame(): void {
  waitingForArena = false
  startAudioUnlocked = true
  window.removeEventListener('pointerdown', unlockStartAudio, true)
  window.removeEventListener('pointerup', unlockStartAudio, true)
  window.removeEventListener('click', unlockStartAudio, true)
  window.removeEventListener('keydown', unlockStartAudio, true)
  startButton?.classList.remove('is-loading')
  startButton?.removeAttribute('aria-busy')
  startOverlay?.classList.add('is-hidden')
  fadeOutAndPause(startBgm, 420)
  playGameBgm()
  startVideo?.play().catch(() => {})
  window.dispatchEvent(new CustomEvent('ping-pong:start'))
}

window.addEventListener('ping-pong:arena-ready', () => {
  arenaReady = true

  if (waitingForArena) {
    enterGame()
  }
})

// 立即注册音频解锁监听，让用户在 Loading 阶段任何点击都能给浏览器一个 user gesture，
// 这样预加载完成、开始页一出现，BGM 就能立刻响起，而不是要等用户先点击开始按钮才有声音。
window.addEventListener('pointerdown', unlockStartAudio, true)
window.addEventListener('pointerup', unlockStartAudio, true)
window.addEventListener('click', unlockStartAudio, true)
window.addEventListener('keydown', unlockStartAudio, true)

// ===== 预加载所有关键资源 =====

interface PreloadAsset {
  url: string
  bytes: number // 估算文件大小，用于计算总进度权重
  label?: string
  skillId?: string // 技能视频专用：用于注册到技能视频缓存
  mediaId?: string // 通用视频/媒体预热标识（开始页视频等）
}

const PRELOAD_ASSETS: PreloadAsset[] = [
  // 核心游戏资源（粗粒度加载提示）
  { url: START_BGM_URL, bytes: 1_300_000, label: '音频资源' },
  { url: GAME_BGM_URL, bytes: 1_900_000 },
  { url: START_BUTTON_SFX_URL, bytes: 60_000 },
  
  { url: '/assets/fonts/ZiTiJiaBaXing.ttf', bytes: 5_200_000, label: '核心字体' },
  
  { url: START_VIDEO_URL, bytes: 3_600_000, label: '过场动画', mediaId: 'start-bg' },
  { url: START_VIDEO_POSTER_URL, bytes: 530_000 },
  { url: '/assets/start/start-button.webp', bytes: 150_000 },
  
  { url: '/assets/gameplay/arena-bg-loop.mp4?v=20260513', bytes: 436_000, label: '图片资源' },
  { url: '/assets/gameplay/arena-bg-loop-poster.jpg?v=20260513', bytes: 130_000 },
  { url: '/assets/gameplay/paddles/ai-paddle-blue.png?v=20260507', bytes: 40_000 },
  { url: '/assets/gameplay/paddles/player-paddle-red.png?v=20260507', bytes: 50_000 },
  { url: '/assets/ai_icon.png', bytes: 35_000 },
  { url: '/assets/human_icon.png', bytes: 35_000 },
  { url: '/assets/ui/skill-icons-sheet.png?v=20260507', bytes: 220_000 },
  { url: '/assets/ui/game-over-panel.png?v=20260507', bytes: 450_000 },
  { url: '/assets/ui/game-over-button.png?v=20260507', bytes: 230_000 },
  { url: '/assets/ui/cursors/game-arrow-red-cursor.png?v=20260507', bytes: 8_000 },
  
  // 技能视频：会被转成 Blob URL + 预热到首帧 ready，避免播放时黑屏
  { url: '/assets/skill-cutins/qianying.mp4?v=20260507', bytes: 3_900_000, label: '技能资源', skillId: 'qianying' },
  { url: '/assets/skill-cutins/flame.mp4?v=20260507', bytes: 4_200_000, skillId: 'flame' },
  { url: '/assets/skill-cutins/thunder.mp4?v=20260507', bytes: 4_100_000, skillId: 'thunder' },
  { url: '/assets/skill-cutins/light.mp4?v=20260507', bytes: 3_800_000, skillId: 'light' },
  { url: '/assets/skill-cutins/serpent.mp4?v=20260507', bytes: 4_000_000, skillId: 'serpent' },
  { url: '/assets/skill-cutins/fold.mp4?v=20260507', bytes: 3_900_000, skillId: 'fold' },
  { url: '/assets/skill-cutins/double.mp4?v=20260507', bytes: 3_700_000, skillId: 'double' },
]

// 视频缓存：把下载好的 mp4 转成 Blob URL，并预热一个隐藏的 video 元素到首帧 ready 状态
// - 技能视频：玩家/AI 释放技能时直接复用，避免 1s 黑屏
// - 开始页视频：进入开始页时立即可播，不再 poster -> 卡顿 -> 播放
const skillCutinBlobUrls: Record<string, string> = {}
const mediaBlobUrls: Record<string, string> = {}
const warmedVideos: Record<string, HTMLVideoElement> = {}

function warmupVideo(key: string, blobUrl: string): Promise<HTMLVideoElement> {
  return new Promise((resolve) => {
    const video = document.createElement('video')
    video.preload = 'auto'
    video.muted = true
    video.playsInline = true
    video.src = blobUrl
    video.style.position = 'absolute'
    video.style.width = '1px'
    video.style.height = '1px'
    video.style.opacity = '0'
    video.style.pointerEvents = 'none'
    video.style.left = '-9999px'
    document.body.appendChild(video)

    let resolved = false
    const finish = (): void => {
      if (resolved) return
      resolved = true
      // 强制 seek 一帧，让浏览器解码出第一帧
      try {
        video.currentTime = 0.001
      } catch {
        // ignore
      }
      warmedVideos[key] = video
      resolve(video)
    }

    video.addEventListener('loadeddata', finish, { once: true })
    video.addEventListener('canplay', finish, { once: true })
    video.addEventListener('error', finish, { once: true })
    // 兜底：5秒内如果没回调，也认为加载完成（避免预加载界面卡死）
    window.setTimeout(finish, 5000)
    video.load()
  })
}

declare global {
  interface Window {
    __pingPongPreload?: {
      getSkillCutinUrl: (skillId: string) => string | null
    }
  }
}

window.__pingPongPreload = {
  getSkillCutinUrl: (skillId: string) => skillCutinBlobUrls[skillId] ?? null,
}

const preloadScreenEl = document.getElementById('preload-screen')
const preloadBarEl = document.getElementById('preload-bar-fill')
const preloadPercentEl = document.getElementById('preload-percent')
const preloadDetailEl = document.getElementById('preload-detail')

const totalBytes = PRELOAD_ASSETS.reduce((sum, a) => sum + a.bytes, 0)
const loadedBytesPerAsset: number[] = PRELOAD_ASSETS.map(() => 0)

function updatePreloadProgress(): void {
  const loaded = loadedBytesPerAsset.reduce((s, n) => s + n, 0)
  const ratio = Math.max(0, Math.min(loaded / totalBytes, 1))
  const percent = Math.floor(ratio * 100)
  if (preloadBarEl) preloadBarEl.style.width = `${percent}%`
  if (preloadPercentEl) preloadPercentEl.textContent = `${percent}%`
}

function setPreloadDetail(text: string): void {
  if (preloadDetailEl) preloadDetailEl.textContent = text
}

async function fetchAssetWithProgress(asset: PreloadAsset, idx: number): Promise<Blob | null> {
  try {
    const response = await fetch(asset.url, { cache: 'force-cache' })
    if (!response.ok || !response.body) {
      loadedBytesPerAsset[idx] = asset.bytes
      updatePreloadProgress()
      return null
    }

    const lengthHeader = response.headers.get('content-length')
    const totalSize = lengthHeader ? Number.parseInt(lengthHeader, 10) || asset.bytes : asset.bytes
    const reader = response.body.getReader()
    const chunks: Uint8Array[] = []
    let loaded = 0

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      if (value) {
        chunks.push(value)
        loaded += value.length
        loadedBytesPerAsset[idx] = Math.min((loaded / totalSize) * asset.bytes, asset.bytes)
        updatePreloadProgress()
      }
    }

    loadedBytesPerAsset[idx] = asset.bytes
    updatePreloadProgress()
    const blob = new Blob(chunks as BlobPart[], { type: 'video/mp4' })

    // 技能视频：转成 Blob URL 并预热 video 到首帧 ready
    if (asset.skillId) {
      const blobUrl = URL.createObjectURL(blob)
      skillCutinBlobUrls[asset.skillId] = blobUrl
      await warmupVideo(`skill-${asset.skillId}`, blobUrl)
    }

    // 通用视频（如开始页）：转成 Blob URL 并预热到首帧 ready
    if (asset.mediaId) {
      const blobUrl = URL.createObjectURL(blob)
      mediaBlobUrls[asset.mediaId] = blobUrl
      await warmupVideo(`media-${asset.mediaId}`, blobUrl)
    }

    return blob
  } catch {
    loadedBytesPerAsset[idx] = asset.bytes
    updatePreloadProgress()
    return null
  }
}

async function ensureFontReady(): Promise<void> {
  try {
    if (document.fonts && 'load' in document.fonts) {
      await document.fonts.load('1em "ZiTiJiaBaXing"')
      await document.fonts.ready
    }
  } catch {
    // Font load may fail in some browsers, just continue.
  }
}

async function runPreload(): Promise<void> {
  const startTs = performance.now()
  console.log('[preload] start')
  setPreloadDetail('正在加载资源…')

  // 控制并发数量。HTTP/2 下提高并发能更好地利用带宽
  const concurrency = 8
  let cursor = 0

  const worker = async (): Promise<void> => {
    while (cursor < PRELOAD_ASSETS.length) {
      const idx = cursor++
      const asset = PRELOAD_ASSETS[idx]
      if (asset.label) setPreloadDetail(`正在加载：${asset.label}`)
      await fetchAssetWithProgress(asset, idx)
    }
  }

  await Promise.all(Array.from({ length: concurrency }, worker))
  await ensureFontReady()

  setPreloadDetail('准备就绪')
  // 全部到位后，让进度条最终定格在 100%
  loadedBytesPerAsset.forEach((_, i) => (loadedBytesPerAsset[i] = PRELOAD_ASSETS[i].bytes))
  updatePreloadProgress()

  const elapsedMs = Math.round(performance.now() - startTs)
  console.log(`[preload] done ${elapsedMs}ms`)
  window.dispatchEvent(new CustomEvent('preload:done', { detail: { ms: elapsedMs } }))
}

function hidePreloadScreen(): void {
  if (!preloadScreenEl) return
  preloadScreenEl.classList.add('is-hidden')
  window.setTimeout(() => preloadScreenEl?.remove(), 600)
}

/** 等待 video 元素的第一帧解码完成（loadeddata / canplay 之一）。最多等 timeoutMs 兜底。*/
function waitVideoFrameReady(video: HTMLVideoElement, timeoutMs = 3000): Promise<void> {
  if (video.readyState >= 2 /* HAVE_CURRENT_DATA */) return Promise.resolve()
  return new Promise((resolve) => {
    let done = false
    const finish = (): void => {
      if (done) return
      done = true
      video.removeEventListener('loadeddata', finish)
      video.removeEventListener('canplay', finish)
      video.removeEventListener('error', finish)
      resolve()
    }
    video.addEventListener('loadeddata', finish, { once: true })
    video.addEventListener('canplay', finish, { once: true })
    video.addEventListener('error', finish, { once: true })
    window.setTimeout(finish, timeoutMs)
  })
}

/** 尝试播放 video，失败时短暂等待后重试，最多 maxAttempts 次 */
async function tryPlayVideoWithRetry(video: HTMLVideoElement, maxAttempts = 3): Promise<boolean> {
  video.defaultMuted = true
  video.muted = true
  video.autoplay = true
  video.playsInline = true
  video.setAttribute('muted', '')
  video.setAttribute('autoplay', '')
  video.setAttribute('playsinline', '')
  video.setAttribute('webkit-playsinline', '')

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      await video.play()
      return true
    } catch {
      if (attempt < maxAttempts) {
        await new Promise((r) => setTimeout(r, 120))
      }
    }
  }
  return false
}

/** 把开始页 video 切到预热好的 Blob URL，并保证首帧已经准备好。失败时返回 false 让外层兜底。*/
async function primeStartVideo(): Promise<boolean> {
  if (!startVideo) return false

  const cachedUrl = mediaBlobUrls['start-bg']
  if (cachedUrl && startVideo.src !== cachedUrl) {
    startVideo.src = cachedUrl
  } else if (!cachedUrl && !startVideo.src) {
    // 兜底：缓存没准备好时回退到远程 URL
    startVideo.src = START_VIDEO_URL
  }
  startVideo.defaultMuted = true
  startVideo.muted = true
  startVideo.autoplay = true
  startVideo.loop = true
  startVideo.playsInline = true
  startVideo.preload = 'auto'
  startVideo.setAttribute('muted', '')
  startVideo.setAttribute('autoplay', '')
  startVideo.setAttribute('playsinline', '')
  startVideo.setAttribute('webkit-playsinline', '')
  try {
    startVideo.load()
    startVideo.currentTime = 0
  } catch {
    // ignore
  }

  await waitVideoFrameReady(startVideo)

  return tryPlayVideoWithRetry(startVideo)
}

/** 当 startVideo 未能自动播放时，第一次用户交互时立即重试播放。 */
function ensureStartVideoOnFirstInteraction(): void {
  if (!startVideo) return

  const tryRecover = async (): Promise<void> => {
    if (!startVideo || !startVideo.paused) {
      cleanup()
      return
    }
    const ok = await tryPlayVideoWithRetry(startVideo, 2)
    if (ok) cleanup()
  }

  const cleanup = (): void => {
    window.removeEventListener('pointerdown', tryRecover, true)
    window.removeEventListener('pointerup', tryRecover, true)
    window.removeEventListener('click', tryRecover, true)
    window.removeEventListener('keydown', tryRecover, true)
    window.removeEventListener('touchstart', tryRecover, true)
  }

  window.addEventListener('pointerdown', tryRecover, true)
  window.addEventListener('pointerup', tryRecover, true)
  window.addEventListener('click', tryRecover, true)
  window.addEventListener('keydown', tryRecover, true)
  window.addEventListener('touchstart', tryRecover, true)
}

function bootStartScreen(): void {
  initPingPongSidePanel()
  setupMobilePcPanelDrawers()
  setupMobilePcSidePanelStateBridge()

  // 创建游戏实例
  new Phaser.Game(config)

  // 进入开始页时立刻尝试播放 BGM。如果用户在 Loading 阶段已经点击过任意位置，
  // 此时 audio 已经解锁；如果还没点击过，会被浏览器拦截，等下次用户交互时
  // unlockStartAudio 监听器会自动重试。
  void playStartBgm()
  window.addEventListener('ping-pong:skill-cutin-start', duckGameBgm)
  window.addEventListener('ping-pong:skill-cutin-end', restoreGameBgm)
  window.addEventListener('ping-pong:return-hit', duckGameBgmForReturnHit)
  window.addEventListener('ping-pong:replay-click', () => {
    playStartButtonSfx()
    playGameBgm()
  })

  startButton?.addEventListener('click', () => {
    requestLandscapeMode()

    if (!startAudioUnlocked) {
      void playStartBgm()
    }

    playStartButtonSfx()

    if (arenaReady) {
      enterGame()
      return
    }

    waitingForArena = true
    startButton.classList.add('is-loading')
    startButton.setAttribute('aria-busy', 'true')
    window.dispatchEvent(new CustomEvent('ping-pong:prepare-arena'))
  })
}

void runPreload().then(async () => {
  // 先把开始页视频接到 Blob URL 并解码第一帧，确保用户看到的是动态画面而不是 poster 卡顿
  const playedOk = await primeStartVideo()
  bootStartScreen()
  hidePreloadScreen()

  // 兜底：如果首次自动播放被浏览器拦截，注册一个一次性监听器，
  // 等用户首次任意交互时立即重试播放，确保不会停在 poster 静态图。
  if (!playedOk) {
    ensureStartVideoOnFirstInteraction()
  }
})
